<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';
import DeliveryZonesAPI from 'dashboard/api/deliveryZones';

const zones = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const editingZone = ref(null);

// Pagination
const currentPage = ref(1);
const perPage = 5;

const totalPages = computed(() => Math.ceil(zones.value.length / perPage));

const paginatedZones = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  const end = start + perPage;
  return zones.value.slice(start, end);
});

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Form fields
const zoneName = ref('');
const zoneFee = ref('');

const fetchZones = async () => {
  try {
    isLoading.value = true;
    const response = await DeliveryZonesAPI.get();
    zones.value = response.data;
    // Ensure current page is valid after fetch
    if (currentPage.value > totalPages.value && totalPages.value > 0) {
      currentPage.value = totalPages.value;
    }
  } catch (error) {
    console.error('Error fetching zones:', error);
    useAlert('Erro ao carregar zonas de entrega');
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchZones();
});

const resetForm = () => {
  zoneName.value = '';
  zoneFee.value = '';
  editingZone.value = null;
  showForm.value = false;
};

const openNewForm = () => {
  resetForm();
  showForm.value = true;
};

const editZone = zone => {
  editingZone.value = zone;
  zoneName.value = zone.name;
  zoneFee.value = zone.fee.toString();
  showForm.value = true;
};

const handleSubmit = async () => {
  if (!zoneName.value.trim()) {
    useAlert('Nome do bairro/zona é obrigatório');
    return;
  }

  const fee = parseFloat(zoneFee.value) || 0;

  try {
    if (editingZone.value) {
      await DeliveryZonesAPI.update(editingZone.value.id, {
        name: zoneName.value.trim(),
        fee: fee,
      });
      useAlert('Zona de entrega atualizada com sucesso!');
    } else {
      await DeliveryZonesAPI.create({
        name: zoneName.value.trim(),
        fee: fee,
      });
      useAlert('Zona de entrega criada com sucesso!');
    }
    resetForm();
    fetchZones();
  } catch (error) {
    console.error('Error saving zone:', error);
    useAlert('Erro ao salvar zona de entrega');
  }
};

const toggleZoneActive = async zone => {
  try {
    await DeliveryZonesAPI.toggleActive(zone.id);
    fetchZones();
    useAlert(zone.active ? 'Zona desativada' : 'Zona ativada');
  } catch (error) {
    console.error('Error toggling zone:', error);
    useAlert('Erro ao alterar status da zona');
  }
};

const deleteZone = async zone => {
  if (!confirm(`Deseja realmente excluir a zona "${zone.name}"?`)) {
    return;
  }

  try {
    await DeliveryZonesAPI.delete(zone.id);
    useAlert('Zona de entrega excluída com sucesso!');
    fetchZones();
  } catch (error) {
    console.error('Error deleting zone:', error);
    useAlert('Erro ao excluir zona de entrega');
  }
};

const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};
</script>

<template>
  <SectionLayout
    title="Zonas de Entrega"
    description="Cadastre os bairros/zonas de entrega e suas respectivas taxas."
    with-border
  >
    <div class="space-y-4">
      <!-- Add button -->
      <div class="flex justify-end">
        <NextButton
          v-if="!showForm"
          blue
          sm
          label="Nova Zona"
          icon="i-lucide-plus"
          @click="openNewForm"
        />
      </div>

      <!-- Form -->
      <div
        v-if="showForm"
        class="border border-n-weak rounded-lg p-4 bg-n-alpha-1"
      >
        <h4 class="text-sm font-medium text-n-slate-12 mb-4">
          {{ editingZone ? 'Editar Zona' : 'Nova Zona de Entrega' }}
        </h4>
        <form class="grid gap-4" @submit.prevent="handleSubmit">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <WithLabel label="Nome do Bairro/Zona">
              <NextInput
                v-model="zoneName"
                placeholder="Ex: Centro, Jardim América..."
                class="w-full"
              />
            </WithLabel>

            <WithLabel label="Taxa de Entrega (R$)">
              <NextInput
                v-model="zoneFee"
                type="number"
                step="0.50"
                min="0"
                placeholder="0.00"
                class="w-full"
              />
            </WithLabel>
          </div>

          <div class="flex gap-2">
            <NextButton blue type="submit" sm label="Salvar" />
            <NextButton
              faded
              slate
              type="button"
              sm
              label="Cancelar"
              @click="resetForm"
            />
          </div>
        </form>
      </div>

      <!-- Zones list -->
      <div v-if="isLoading" class="text-center py-4">
        <span class="text-n-slate-11">Carregando...</span>
      </div>

      <div
        v-else-if="zones.length === 0"
        class="text-center py-8 text-n-slate-11"
      >
        <span class="i-lucide-map-pin size-8 mb-2 opacity-50" />
        <p>Nenhuma zona de entrega cadastrada.</p>
        <p class="text-sm">
          Clique em "Nova Zona" para adicionar bairros e taxas.
        </p>
      </div>

      <div v-else class="border border-n-weak rounded-lg overflow-hidden">
        <table class="w-full">
          <thead class="bg-n-alpha-2">
            <tr>
              <th
                class="text-left text-xs font-medium text-n-slate-11 uppercase px-4 py-3"
              >
                Bairro/Zona
              </th>
              <th
                class="text-right text-xs font-medium text-n-slate-11 uppercase px-4 py-3"
              >
                Taxa
              </th>
              <th
                class="text-center text-xs font-medium text-n-slate-11 uppercase px-4 py-3"
              >
                Status
              </th>
              <th
                class="text-right text-xs font-medium text-n-slate-11 uppercase px-4 py-3"
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="zone in paginatedZones"
              :key="zone.id"
              :class="{ 'opacity-50': !zone.active }"
            >
              <td class="px-4 py-3 text-sm text-n-slate-12">
                {{ zone.name }}
              </td>
              <td class="px-4 py-3 text-sm text-n-slate-12 text-right">
                {{ formatCurrency(zone.fee) }}
              </td>
              <td class="px-4 py-3 text-center">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="[
                    zone.active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
                  ]"
                >
                  {{ zone.active ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td class="px-4 py-3 text-right">
                <div class="flex justify-end gap-2">
                  <button
                    type="button"
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                    :title="zone.active ? 'Desativar' : 'Ativar'"
                    @click="toggleZoneActive(zone)"
                  >
                    <span
                      class="size-4"
                      :class="[
                        zone.active ? 'i-lucide-eye-off' : 'i-lucide-eye',
                      ]"
                    />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                    title="Editar"
                    @click="editZone(zone)"
                  >
                    <span class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-n-slate-11 hover:text-red-600 transition-colors"
                    title="Excluir"
                    @click="deleteZone(zone)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between px-4 py-3 border-t border-n-weak bg-n-alpha-1"
        >
          <div class="text-sm text-n-slate-11">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a
            {{ Math.min(currentPage * perPage, zones.length) }} de
            {{ zones.length }} registros
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              :disabled="currentPage === 1"
              class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="prevPage"
            >
              <span class="i-lucide-chevron-left size-4" />
            </button>
            <button
              v-for="page in totalPages"
              :key="page"
              type="button"
              class="px-3 py-1 rounded text-sm transition-colors"
              :class="[
                currentPage === page
                  ? 'bg-woot-500 text-white'
                  : 'hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
            <button
              type="button"
              :disabled="currentPage === totalPages"
              class="p-1.5 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="nextPage"
            >
              <span class="i-lucide-chevron-right size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
